import { ProductNodeProduct } from './canvas/ProductNode';

/**
 * Map an API product onto the shape the canvas needs.
 *
 * The input shape varies: `productsApi.findAllProducts()` runs results through
 * `Product.prepareProduct()`, which flattens `organization.applicationTypes`
 * into `application_types` and can nest visualization under
 * `visualization.data[0]`. Every path is read here so callers don't have to
 * know which variant they were handed.
 *
 * Shared by the product picker and the planner, which both turn raw products
 * into canvas nodes and would otherwise drift apart.
 */
export function toCanvasProduct(raw: any): ProductNodeProduct & { sku?: string } {
    const viz = raw?.visualization;
    const flat = viz?.data?.[0];

    return {
        uuid: raw?.uuid,
        name: raw?.name,
        sku: raw?.sku,
        thumbnailPath: raw?.thumbnailPath,
        visualizationType: viz?.type || flat?.type,
        applicationTypes: raw?.application_types || raw?.organization?.applicationTypes,
        // 3D model for furniture — drives the inline rotation widget on the
        // Product Photography recipe.
        modelPath: viz?.model3d?.modelPath,
        texturePath: viz?.model3d?.texturePath,
        // The source photograph of a furniture piece — for most of them the only
        // image that exists, and what a moodboard cut-out is made from.
        originalImagePath: viz?.model3d?.originalImagePath || flat?.model3d?.originalImagePath,
        // Seamless material for surface products; falls back to the thumbnail
        // so the 3D studio always has something to map.
        surfaceTexturePath:
            viz?.surface?.materialPath
            || viz?.surface?.materialPaths?.[0]
            || flat?.surface?.materialPath
            || flat?.surface?.materialPaths?.[0]
            || raw?.thumbnailPath,
        // Top-level category slug (e.g. 'rugs') — flags flat floor coverings so
        // the 3D studio lays them flat.
        category: raw?.category,
        galleryImages: Array.isArray(raw?.galleryImages) ? raw.galleryImages : [],
        largestDimensionM: largestDimensionM(raw),
    };
}

const TO_METRES: Record<string, number> = { mm: 0.001, cm: 0.01, m: 1, in: 0.0254, ft: 0.3048 };

/**
 * The product's largest real dimension, in metres — what a GLB is scaled to
 * when it is placed in a room.
 *
 * Read from the raw size axis where it survives: the prepared product maps
 * "length" from the axis' height, which for a rug is its 2 cm thickness, not
 * its length. The largest of the three is the same answer whichever way a
 * vendor filled them in.
 */
function largestDimensionM(raw: any): number | undefined {
    const size = raw?.axes?.size;
    const unit = String(size?.unit || raw?.unit || 'cm').toLowerCase();
    const factor = TO_METRES[unit] ?? 0.01;
    const dims = size
        ? [size.width, size.height, size.depth]
        : [raw?.width, raw?.length];
    const largest = Math.max(0, ...dims.map(Number).filter((n) => Number.isFinite(n) && n > 0));
    return largest > 0 ? largest * factor : undefined;
}
